import { useEffect, useRef, useState } from "react";
import {
  Bell, Briefcase, ChevronDown, ExternalLink, Lock, LogOut, Monitor, Moon, Settings2, Shield,
  Sun, User,
} from "lucide-react";
import {
  Avatar, AvatarFallback, AvatarImage, Button, cn, userInitials,
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@wa/ui";
import { crmUrl } from "@wa/core/crm-access";
import { useT } from "../../i18n/I18nProvider";
import { ADMIN_URL, withLang, type AccountUser } from "../../lib/auth";
import { applyThemeMode, readThemeMode, type ThemeMode } from "../../lib/themeMode";
import type { TabKey } from "../tabs";
import { LanguageList, LanguageRow } from "./LanguageChoice";
import { MENU_ICON, MENU_PANEL, MENU_ROW, MENU_SEP } from "./menuStyles";
import { Seg } from "./Seg";
import type { AdminLink } from "./AccountHeader";

const THEMES: { value: ThemeMode; icon: typeof Sun; titleKey: string }[] = [
  { value: "system", icon: Monitor, titleKey: "preferences.themeSystem" },
  { value: "light", icon: Sun, titleKey: "preferences.themeLight" },
  { value: "dark", icon: Moon, titleKey: "preferences.themeDark" },
];

/**
 * The account dropdown in the hub header - the same panel the CRM carries
 * (`web_crm/src/crm/shell/AccountMenu.tsx`), in this app's vocabulary.
 *
 * It gathers everything about the signed-in PERSON in one place named after
 * them: who they are, the sibling apps they may open, their own screens, and
 * the two switches that used to sit loose in the header row.
 *
 * A kit DropdownMenu, so Radix owns the keyboard, Escape, focus return and
 * outside clicks, and every row is a menu item the arrow keys reach. Theme and
 * language are switches, not destinations: picking one keeps the menu open
 * (`preventDefault` in `onSelect`).
 */
export function AccountMenu({
  user,
  portalBrand,
  adminLink,
  crmLink,
  openTabs,
  caption,
  compact = false,
  allScreens = false,
  onGo,
  onLogout,
}: {
  user: AccountUser;
  /** The operator's client portal rather than Husly's own hub. */
  portalBrand: boolean;
  adminLink: AdminLink;
  crmLink: boolean;
  /** The account tabs this visit may open - a row is drawn only for these. */
  openTabs: TabKey[];
  /**
   * The line under the name on the trigger. The portal passes the company the
   * person represents, which says more than the word "Portal" does.
   */
  caption?: string;
  /**
   * Keep the trigger down to the avatar at every width. The portal's top bar
   * holds up to seven service links in a row that never grows past
   * `max-w-6xl`, so the name beside the avatar has no width where it fits
   * next to all of them. The panel still opens with the name and the caption.
   */
  compact?: boolean;
  /**
   * Draw a row for EVERY personal screen, not only the three the hub shows.
   * The hub has a side menu for the rest; the portal has none, so this panel
   * is the only door to the notification settings and to the privacy screen -
   * and the second one carries the data export and the account deletion.
   */
  allScreens?: boolean;
  onGo: (tab: TabKey) => void;
  onLogout: () => void;
}) {
  const t = useT();
  const [theme, setTheme] = useState<ThemeMode>(() => readThemeMode());
  // The panel has two views. The language list replaces the rows rather than
  // hanging off them (see `LanguageRow`). Every opening starts at the root.
  const [view, setView] = useState<"root" | "locale">("root");
  const contentRef = useRef<HTMLDivElement>(null);
  const switched = useRef(false);

  function show(next: "root" | "locale") {
    switched.current = true;
    setView(next);
  }

  // The row that switched the view has just unmounted and taken focus with it,
  // which leaves the menu deaf to the arrow keys. Hand focus to the way back in
  // the list, or to the language row on the way out.
  useEffect(() => {
    if (!switched.current) return;
    switched.current = false;
    const panel = contentRef.current;
    const target = view === "locale" ? "[role^=menuitem]" : "[data-language-row]";
    panel?.querySelector<HTMLElement>(target)?.focus();
  }, [view]);

  const name = user.name || user.email;

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) setView("root");
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          aria-label={t("app.title")}
          className={cn(
            "group h-11 shrink-0 gap-2.5 rounded-full bg-card/70 py-[3px] pl-[3px] pr-3 font-normal active:scale-100 data-[state=open]:bg-accent",
            compact ? "pr-[3px]" : "max-sm:pr-[3px]",
          )}
        >
          <Avatar className="size-[38px] shrink-0">
            {user.avatarUrl ? <AvatarImage src={user.avatarUrl} alt="" /> : null}
            <AvatarFallback className="bg-primary/15 text-[12.5px] font-extrabold uppercase text-primary">
              {userInitials(user.name, user.email)}
            </AvatarFallback>
          </Avatar>
          <span className={cn("max-w-[160px] text-left", compact ? "hidden" : "max-sm:hidden")}>
            <span className="block truncate text-[13.25px] font-semibold leading-[1.15]">{name}</span>
            <span className="mt-px block truncate text-[11px] text-muted-foreground">
              {caption ?? (portalBrand ? t("app.badgePortal") : t("app.badge"))}
            </span>
          </span>
          <ChevronDown
            className={cn(
              "size-3! shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180",
              compact ? "hidden" : "max-sm:hidden",
            )}
          />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        ref={contentRef}
        align="end"
        sideOffset={8}
        collisionPadding={8}
        aria-label={t("app.title")}
        className={cn(MENU_PANEL, "max-h-(--radix-dropdown-menu-content-available-height) w-[286px] overflow-y-auto")}
      >
        {view === "locale" ? (
          <LanguageList onBack={() => show("root")} />
        ) : (
        <>
        <div className="mb-1.5 border-b border-border px-3 pb-2.5 pt-1">
          <div className="truncate text-[13.25px] font-bold tracking-tight">{name}</div>
          <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{user.email}</div>
          {/* The company is on the trigger only where the trigger has room
              for words; a compact trigger is the avatar, so it lives here. */}
          {compact && caption && (
            <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{caption}</div>
          )}
        </div>

        {adminLink && (
          <DropdownMenuItem asChild className={MENU_ROW}>
            <a href={withLang(ADMIN_URL)}>
              <ExternalLink className={MENU_ICON} />
              {t(adminLink === "cms" ? "app.openCms" : "app.openEditor")}
            </a>
          </DropdownMenuItem>
        )}
        {crmLink && (
          <DropdownMenuItem asChild className={MENU_ROW}>
            <a href={crmUrl(window.location)}>
              <Briefcase className={MENU_ICON} />
              {t("app.openCrm")}
            </a>
          </DropdownMenuItem>
        )}
        {openTabs.includes("profile") && (
          <DropdownMenuItem className={MENU_ROW} onSelect={() => onGo("profile")}>
            <User className={MENU_ICON} />
            {t("tabs.profile")}
          </DropdownMenuItem>
        )}
        {openTabs.includes("preferences") && (
          <DropdownMenuItem className={MENU_ROW} onSelect={() => onGo("preferences")}>
            <Settings2 className={MENU_ICON} />
            {t("tabs.preferences")}
          </DropdownMenuItem>
        )}
        {/* The security screen, which is where passkeys are managed among the
            password, the sessions and the sign-in log. It is named after the
            screen rather than after one thing on it, and it carries the same
            name and the same shield as the tab in the side menu. */}
        {openTabs.includes("security") && (
          <DropdownMenuItem className={MENU_ROW} onSelect={() => onGo("security")}>
            <Shield className={MENU_ICON} />
            {t("tabs.security")}
          </DropdownMenuItem>
        )}
        {allScreens && openTabs.includes("notifications") && (
          <DropdownMenuItem className={MENU_ROW} onSelect={() => onGo("notifications")}>
            <Bell className={MENU_ICON} />
            {t("tabs.notifications")}
          </DropdownMenuItem>
        )}
        {allScreens && openTabs.includes("privacy") && (
          <DropdownMenuItem className={MENU_ROW} onSelect={() => onGo("privacy")}>
            <Lock className={MENU_ICON} />
            {t("tabs.privacy")}
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator className={MENU_SEP} />

        <div className="flex items-center gap-2.5 py-1 pl-3 pr-1.5">
          <span className="flex min-w-0 flex-1 items-center gap-2.5 whitespace-nowrap text-[12.75px] font-medium">
            <Moon className={MENU_ICON} />
            {t("preferences.theme")}
          </span>
          <Seg
            label={t("preferences.theme")}
            value={theme}
            onChange={(v) => {
              applyThemeMode(v);
              setTheme(v);
            }}
            options={THEMES.map((m) => {
              const Icon = m.icon;
              return { value: m.value, title: t(m.titleKey), label: <Icon className="size-3" /> };
            })}
          />
        </div>

        <LanguageRow onOpen={() => show("locale")} />

        <DropdownMenuSeparator className={MENU_SEP} />

        <DropdownMenuItem
          destructive
          onSelect={onLogout}
          className={cn(MENU_ROW, "text-destructive focus:bg-destructive/10 focus:text-destructive")}
        >
          <LogOut className="size-4 shrink-0 text-destructive" />
          {t("app.logout")}
        </DropdownMenuItem>
        </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
